import React, { createContext, useContext, useEffect, useState } from 'react';
import { pb } from '../pbClient';
import { UserProfile, UserRole } from '../types';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isSuperuser: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const buildProfile = (model: any): UserProfile | null => {
    if (!model) return null;
    return {
      id: model.id,
      username: model.username || model.name || model.email,
      email: model.email,
      role: (model.role as UserRole) || 'user',
      banned: model.banned ?? false,
      created_at: model.created,
    };
  };

  const checkAndSetUser = (model: any) => {
    if (!model) {
      setUser(null);
      return;
    }
    const profile = buildProfile(model);
    if (profile?.banned) {
      pb.authStore.clear();
      setUser(null);
      alert('Account suspended. Please contact administration.');
      return;
    }
    setUser(profile);
  };

  const refreshProfile = async () => {
    if (!pb.authStore.isValid || !pb.authStore.model) return;
    try {
      const record = await pb.collection('users').getOne(pb.authStore.model.id);
      checkAndSetUser(record);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    // Restore from persisted auth store
    checkAndSetUser(pb.authStore.model);
    setLoading(false);

    // Listen for auth state changes
    const unsub = pb.authStore.onChange((_token, model) => {
      checkAndSetUser(model);
    });

    return () => unsub();
  }, []);

  const signOut = async () => {
    pb.authStore.clear();
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';
  const isSuperuser = user?.role === 'superuser' || user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, loading, signOut, isAdmin, isSuperuser, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
